import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M05ProductDetailScreen'>;

export const M05ProductDetailScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();

  const handleCreateIllustration = () => {
    navigation.navigate('M06InputCustomerScreen');
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        {/* Banner Area */}
        <View style={styles.banner}>
          <Icon name="calculate" size={48} color="#ffffff" />
          <Text style={styles.bannerTitle}>ManulifeMOVE</Text>
          <Text style={styles.bannerSub}>An Phát Đầu Tư Thịnh Vượng</Text>
        </View>

        {/* Product Details Content */}
        <View style={styles.content}>
          <View style={styles.infoCard}>
            <Text style={styles.infoCardTitle}>📋 Thông tin chính</Text>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>• Loại sản phẩm:</Text>
              <Text style={styles.infoValue}>Bảo hiểm liên kết đơn vị</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>• Độ tuổi bảo vệ:</Text>
              <Text style={styles.infoValue}>15 - 65 tuổi</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>• Thời hạn đóng phí:</Text>
              <Text style={styles.infoValue}>10 / 15 / 20 năm hoặc trọn đời</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>• Phí đóng tối thiểu:</Text>
              <Text style={styles.infoValue}>12.000.000 VNĐ / năm</Text>
            </View>
          </View>

          {/* Quyền lợi nổi bật */}
          <Text style={styles.sectionTitle}>🎯 Quyền lợi nổi bật</Text>
          <View style={styles.benefitList}>
            <View style={styles.benefitItem}>
              <Icon name="check_circle" size={20} color={colors.secondary} />
              <Text style={styles.benefitText}>Bảo vệ tối đa 175% Số tiền bảo hiểm trước các rủi ro</Text>
            </View>
            <View style={styles.benefitItem}>
              <Icon name="check_circle" size={20} color={colors.secondary} />
              <Text style={styles.benefitText}>Đầu tư linh hoạt qua 4 quỹ chuyên nghiệp (cổ phiếu, trái phiếu...)</Text>
            </View>
            <View style={styles.benefitItem}>
              <Icon name="check_circle" size={20} color={colors.secondary} />
              <Text style={styles.benefitText}>Nhận khoản thưởng duy trì hợp đồng định kỳ hấp dẫn</Text>
            </View>
            <View style={styles.benefitItem}>
              <Icon name="check_circle" size={20} color={colors.secondary} />
              <Text style={styles.benefitText}>Hỗ trợ chi phí thai sản và chăm sóc y tế bổ sung (nếu kèm Rider)</Text>
            </View>
            <View style={styles.benefitItem}>
              <Icon name="check_circle" size={20} color={colors.secondary} />
              <Text style={styles.benefitText}>Miễn đóng phí hợp đồng chính khi người được bảo hiểm gặp rủi ro hiểm nghèo</Text>
            </View>
          </View>

          {/* Mô tả chi tiết */}
          <Text style={styles.sectionTitle}>📖 Mô tả sản phẩm</Text>
          <Text style={styles.descText}>
            ManulifeMOVE là giải pháp bảo hiểm liên kết đầu tư ưu việt, kết hợp nhu cầu bảo vệ tài chính trước rủi ro 
            và gia tăng tích lũy tài sản dài hạn. Khách hàng có quyền lựa chọn tỷ lệ đầu tư vào các quỹ cổ phiếu tăng trưởng 
            hoặc quỹ trái phiếu an toàn tùy theo khẩu vị rủi ro cá nhân. Sản phẩm còn tích lũy điểm thưởng thông qua 
            các hoạt động thể chất hàng ngày qua app di động ManulifeMOVE.
          </Text>

          {/* Phù hợp với ai */}
          <Text style={styles.sectionTitle}>💡 Đối tượng phù hợp</Text>
          <View style={styles.fitList}>
            <Text style={styles.fitText}>• Người trong độ tuổi lao động từ 30-50 tuổi có thu nhập ổn định.</Text>
            <Text style={styles.fitText}>• Các gia đình trẻ cần giải pháp bảo vệ tài chính trụ cột.</Text>
            <Text style={styles.fitText}>• Khách hàng có mong muốn tích lũy tài sản kết hợp đầu tư tài chính nhàn rỗi.</Text>
          </View>
        </View>
      </ScrollView>

      {/* CTA Button at Bottom */}
      <View style={styles.bottomBar}>
        <TouchableOpacity style={styles.ctaBtn} onPress={handleCreateIllustration}>
          <Text style={styles.ctaBtnText}>TẠO MINH HỌA NGAY</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollContainer: {
    paddingBottom: 88, // Space for sticky bottom bar
  },
  banner: {
    backgroundColor: colors.primary,
    paddingVertical: spacing[8],
    paddingHorizontal: spacing[4],
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerTitle: {
    fontFamily: "'Be Vietnam Pro', sans-serif",
    fontSize: 24,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
    marginTop: spacing[2],
  },
  bannerSub: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
    marginTop: spacing[1],
  },
  content: {
    padding: spacing[4],
  },
  infoCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    marginBottom: spacing[5],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  infoCardTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[3],
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing[2],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  infoLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  infoValue: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginTop: spacing[2],
    marginBottom: spacing[3],
  },
  benefitList: {
    gap: spacing[2.5],
    marginBottom: spacing[5],
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2.5],
  },
  benefitText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    color: colors.onSurface,
  },
  descText: {
    fontSize: 13,
    lineHeight: 20,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[5],
    textAlign: 'justify',
  },
  fitList: {
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  fitText: {
    fontSize: 13,
    lineHeight: 18,
    color: colors.onSurface,
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    padding: spacing[4],
    borderTopWidth: 1,
    borderTopColor: colors.outlineVariant,
  },
  ctaBtn: {
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaBtnText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.5,
  },
});
